/**
 * @fileoverview React хук для парсинга URL параметров.
 * Предоставляет удобный способ доступа к query параметрам без зависимости от роутера.
 * Включает специальную обработку HTML-кодированных амперсандов.
 * 
 * @since 1.0.0
 */

import { useMemo } from 'react';

/**
 * Хук для извлечения и парсинга URL параметров.
 * 
 * Извлекает параметры из window.location.search и преобразует их в объект
 * с парами ключ-значение. Поддерживает декодирование HTML-сущностей и
 * мемоизацию для оптимизации производительности.
 * 
 * @returns Объект с URL параметрами где ключи и значения являются строками
 * 
 * @remarks
 * **Особенности реализации:**
 * - Использует нативный URLSearchParams для надежного парсинга
 * - Автоматически декодирует `&amp;` в `&` для работы с HTML-кодированными URL
 * - Безопасен для SSR (возвращает пустую строку если window недоступен)
 * - Мемоизирован для предотвращения лишних пересчетов
 * 
 * **Типичные URL параметры в приложении:**
 * - `operation_id` - идентификатор операции
 * - `task_element_id` - идентификатор элемента задачи  
 * - `doer_user_id` - идентификатор пользователя-исполнителя
 * - `min_size` - минимальное количество фотографий
 * - `max_size` - максимальное количество фотографий
 * 
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * // URL: https://example.com/?operation_id=123&task_element_id=456&min_size=2
 * function MyComponent() {
 *   const params = useQueryParams();
 *   
 *   console.log(params.operation_id);    // "123"
 *   console.log(params.task_element_id); // "456" 
 *   console.log(params.min_size);        // "2"
 *   console.log(params.nonexistent);     // undefined
 *   
 *   return <div>Operation: {params.operation_id}</div>;
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Обработка HTML-кодированных URL
 * // URL: https://example.com/?param1=value1&amp;param2=value2
 * const params = useQueryParams();
 * // Корректно парсит оба параметра несмотря на &amp;
 * console.log(params.param1); // "value1"
 * console.log(params.param2); // "value2"
 * ```
 */
export const useQueryParams = () => {
  // Используем useMemo для мемоизации результата
  const params = useMemo(() => {
    // Используем нативный window.location вместо react-router-dom
    const search = typeof window !== 'undefined' ? window.location.search : '';
    
    // Декодируем HTML-кодированные амперсанды перед парсингом
    const decodedSearch = search.replace(/&amp;/g, '&');
    const queryParams = new URLSearchParams(decodedSearch);

    const result: Record<string, string> = {};

    queryParams.forEach((value, key) => {
      result[key] = value;
    });

    return result;
  }, [typeof window !== 'undefined' ? window.location.search : '']);

  return params;
}; 